import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { useEffect } from "react";
import * as gtag from "@/utils/gtag";
import { verifyGoogleTag } from "@/utils/debugAnalytics";
import { AuthCallback } from "@/components/auth/AuthCallback";
import Index from "./pages/Index";
import Services from "./pages/Services";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import PersonalInfo from "./pages/PersonalInfo";
import Documents from "./pages/Documents";
import ApplicationForm from "./pages/ApplicationForm";
import Appointment from "./pages/Appointment";
import About from "./pages/About";
import Contact from "./pages/Contact";
import KnowledgeBase from "./pages/KnowledgeBase";
import HelpCenter from "./pages/HelpCenter";
import Resources from "./pages/Resources";
import YourPackage from "./pages/YourPackage";
import ServiceSelection from "./pages/ServiceSelection";
import ReviewApplication from "./pages/ReviewApplication";
import NotFound from "./pages/NotFound";
import WhatsAppButton from "@/components/WhatsAppButton";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import GDPRCompliance from "./pages/GDPRCompliance";

// Service Pages
import TemporaryResidenceCard from "./pages/TemporaryResidenceCard";
import PermanentResidenceCard from "./pages/PermanentResidenceCard";
import EULongTermResidenceCard from "./pages/EULongTermResidenceCard";
import WorkPermit from "./pages/WorkPermit";
import CompanyRegistration from "./pages/CompanyRegistration";
import BusinessIncubator from "./pages/BusinessIncubator";
import TranslationServices from "./pages/TranslationServices";
import Consultation from "./pages/Consultation";
import AuthTest from "./pages/AuthTest";
import BookKeeping from "./pages/BookKeeping";

// Documentation Pages
import Documentation from "./pages/knowledge-base/documentation";
import PassportRequirements from "./pages/knowledge-base/documentation/passport-requirements";
import IncomeProof from "./pages/knowledge-base/documentation/income-proof";
import HousingGuide from "./pages/knowledge-base/documentation/housing-guide";
import HealthInsurance from "./pages/knowledge-base/documentation/health-insurance";

// Processing Times Pages
import ProcessingTimes from "./pages/knowledge-base/processing-times";
import VoivodeshipTimes from "./pages/knowledge-base/processing-times/voivodeship-times";
import TrackStatus from "./pages/knowledge-base/processing-times/track-status";
import ExpeditedOptions from "./pages/knowledge-base/processing-times/expedited-options";
import HandlingDelays from "./pages/knowledge-base/processing-times/handling-delays";

// Getting Started Pages
import GettingStarted from "./pages/knowledge-base/getting-started";
import KartaPobytuRequirements from "./pages/knowledge-base/getting-started/karta-pobytu-requirements";
import DocumentChecklist from "./pages/knowledge-base/getting-started/document-checklist";
import ApplicationProcess from "./pages/knowledge-base/getting-started/application-process";
import CommonMistakes from "./pages/knowledge-base/getting-started/common-mistakes";

// Support Resources Pages
import SupportResources from "./pages/knowledge-base/support-resources";
import OfficeContacts from "./pages/knowledge-base/support-resources/office-contacts";
import LegalSupport from "./pages/knowledge-base/support-resources/legal-support";
import CommunityResources from "./pages/knowledge-base/support-resources/community-resources";
import FAQ from "./pages/knowledge-base/support-resources/faq";

const queryClient = new QueryClient();

// Create a RouteTracker component to handle page view tracking
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Send page view to GA4
    gtag.pageview(location.pathname + location.search);
    
    // Test event for Google Tag
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search
      });
    }

    // Test event for GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search
      });
    }
  }, [location]);

  return null;
};

const App = () => {
  useEffect(() => {
    // Verify Google Tag implementation
    verifyGoogleTag();

    // Send initial page load event
    if (window.gtag) {
      window.gtag('event', 'app_loaded', {
        event_category: 'engagement',
        event_label: 'Initial App Load'
      });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <RouteTracker />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/personal-info" element={<PersonalInfo />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/application-form" element={<ApplicationForm />} />
              <Route path="/appointment" element={<Appointment />} />
              <Route path="/your-package" element={<YourPackage />} />
              <Route path="/service-selection" element={<ServiceSelection />} />
              <Route path="/review-application" element={<ReviewApplication />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/knowledge-base" element={<KnowledgeBase />} />
              <Route path="/help-center" element={<HelpCenter />} />
              <Route path="/resources" element={<Resources />} />
              
              {/* Service Pages */}
              <Route path="/temporary-residence-card" element={<TemporaryResidenceCard />} />
              <Route path="/permanent-residence-card" element={<PermanentResidenceCard />} />
              <Route path="/eu-long-term-residence-card" element={<EULongTermResidenceCard />} />
              <Route path="/work-permit" element={<WorkPermit />} />
              <Route path="/company-registration" element={<CompanyRegistration />} />
              <Route path="/business-incubator" element={<BusinessIncubator />} />
              <Route path="/translation-services" element={<TranslationServices />} />
              <Route path="/consultation" element={<Consultation />} />
              <Route path="/auth-test" element={<AuthTest />} />
              <Route path="/book-keeping" element={<BookKeeping />} />
              
              {/* Getting Started Routes */}
              <Route path="/knowledge-base/getting-started" element={<GettingStarted />} />
              <Route path="/knowledge-base/karta-pobytu-requirements" element={<KartaPobytuRequirements />} />
              <Route path="/knowledge-base/document-checklist" element={<DocumentChecklist />} />
              <Route path="/knowledge-base/application-process" element={<ApplicationProcess />} />
              <Route path="/knowledge-base/common-mistakes" element={<CommonMistakes />} />
              
              {/* Documentation Routes */}
              <Route path="/knowledge-base/documentation" element={<Documentation />} />
              <Route path="/knowledge-base/documentation/passport-requirements" element={<PassportRequirements />} />
              <Route path="/knowledge-base/documentation/income-proof" element={<IncomeProof />} />
              <Route path="/knowledge-base/documentation/housing-guide" element={<HousingGuide />} />
              <Route path="/knowledge-base/documentation/health-insurance" element={<HealthInsurance />} />
              
              {/* Processing Times Routes */}
              <Route path="/knowledge-base/processing-times" element={<ProcessingTimes />} />
              <Route path="/knowledge-base/processing-times/voivodeship-times" element={<VoivodeshipTimes />} />
              <Route path="/knowledge-base/processing-times/track-status" element={<TrackStatus />} />
              <Route path="/knowledge-base/processing-times/expedited-options" element={<ExpeditedOptions />} />
              <Route path="/knowledge-base/processing-times/handling-delays" element={<HandlingDelays />} />
              
              {/* Support Resources Routes */}
              <Route path="/knowledge-base/support-resources" element={<SupportResources />} />
              <Route path="/knowledge-base/support-resources/office-contacts" element={<OfficeContacts />} />
              <Route path="/knowledge-base/support-resources/legal-support" element={<LegalSupport />} />
              <Route path="/knowledge-base/support-resources/community-resources" element={<CommunityResources />} />
              <Route path="/knowledge-base/support-resources/faq" element={<FAQ />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route path="/TermsOfService" element={<TermsOfService />} />
              <Route path="/CookiePolicy" element={<CookiePolicy />} />
              <Route path="/GDPRCompliance" element={<GDPRCompliance />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <WhatsAppButton />
            <SpeedInsights />
            <Analytics />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
